import { useState } from "react";
import type { AssetComment, Highlight } from "../lib/api.ts";
import { formatOffset } from "../lib/timeline.ts";

/**
 * I. The scrubber's annotation track: a marker for every timed comment and a band for every
 * highlight, placed along the clip. Clicking one seeks the player there and shows the comment.
 * Markers are real buttons, so they are reachable and announced without a pointer.
 */
export function AnnotationMarkers({
  durationMs,
  comments,
  highlights,
  name,
  onSeek,
}: {
  durationMs: number | null;
  comments: AssetComment[];
  highlights: Highlight[];
  name: (userId: string) => string;
  onSeek: (ms: number) => void;
}) {
  const [selected, setSelected] = useState<AssetComment | null>(null);
  if (!durationMs || durationMs <= 0) return null;
  const pct = (ms: number) => `${Math.round((ms / durationMs) * 1000) / 10}%`;
  const timed = comments.filter(
    (c): c is AssetComment & { offsetMs: number } =>
      c.offsetMs !== null && c.offsetMs >= 0 && c.offsetMs <= durationMs,
  );
  const bands = highlights.filter((h) => h.startMs < durationMs);
  if (timed.length === 0 && bands.length === 0) return null;
  return (
    <div className="annotations">
      <div
        className="marker-track"
        aria-label="Comments and highlights along the clip"
        role="group"
      >
        {bands.map((h) => (
          <button
            key={h.id}
            type="button"
            className="band"
            style={{ left: pct(h.startMs), width: pct(Math.min(h.endMs, durationMs) - h.startMs) }}
            aria-label={`Highlight${h.label ? ` “${h.label}”` : ""} ${formatOffset(h.startMs)}–${formatOffset(h.endMs)}`}
            onClick={() => onSeek(h.startMs)}
          />
        ))}
        {timed.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`marker${selected?.id === c.id ? " selected" : ""}`}
            style={{ left: pct(c.offsetMs) }}
            aria-label={`Comment at ${formatOffset(c.offsetMs)} by ${name(c.authorId)}: ${c.body}`}
            onClick={() => {
              setSelected(c);
              onSeek(c.offsetMs);
            }}
          />
        ))}
      </div>
      <div role="status" aria-live="polite">
        {selected && (
          <div className="nested small">
            <strong>{name(selected.authorId)}</strong> at {formatOffset(selected.offsetMs ?? 0)}:{" "}
            {selected.body}
          </div>
        )}
      </div>
    </div>
  );
}
